import React, { useState, useEffect, useRef, useMemo, Fragment, useLayoutEffect } from 'react';
import { Box, Typography, Button, Divider, Chip, Collapse, TextField, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText, Checkbox, InputAdornment, Popover } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MapIcon from '@mui/icons-material/Map';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UndoIcon from '@mui/icons-material/Undo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import PlaceIcon from '@mui/icons-material/Place';
import { Virtuoso } from 'react-virtuoso';
import { APPLICATION_TYPES, APPLICATION_TYPE_ORDER, getApplicationStyle, categoryTagsForDisplay, getPriorityApplicationType } from './applicationStyles';
import apiService from '../services/api';
import SourceDocumentsList from './SourceDocumentsList';
import { getApplicationSourceItems } from '../utils/sourceDocuments';
import { getCaseStatusColor, CASE_STATUS_COLORS, STATUS_BADGE_LABELS } from '../utils/caseStatus';
import {
  getTimelineEventList,
  sortEventsByDate,
  eventBubbleDiameter,
  ICON_EVENT_TYPES,
  MILESTONE_EVENT_TYPES,
  markerIconFontSizePx,
} from '../utils/timelineEvents';
import { formatLocalDateString } from '../utils/localDate';
import { addUserActionBreadcrumb } from '../utils/sentryTracking';
import { usePersistedScroll, readStoredScroll, writeStoredScroll } from '../utils/usePersistedScroll';
import { posthog, isUserIdentified } from '../services/posthog';
import { useElementWidth } from '../hooks/useElementWidth';
import { RIGHT_PANEL_COMFORT_W, PANEL_CHROME_HEADER_H } from '../layout/appChrome';

const LIST_SCROLL_KEY = 'ls_rightPanelListScroll';
const LIST_FOCUS_KEY = 'ls_rightPanelListFocus';

function readListFocus() {
  try {
    const raw = sessionStorage.getItem(LIST_FOCUS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeListFocus(payload) {
  try {
    if (!payload) {
      sessionStorage.removeItem(LIST_FOCUS_KEY);
      return;
    }
    sessionStorage.setItem(LIST_FOCUS_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

// Theme colors matching "light mode"
const colors = {
  bg: '#ffffff',
  surface: '#f8f9fa',
  border: '#e5e7eb',
  text: '#111827',
  textSecondary: '#6b7280',
  primary: '#7454A5',
  primaryHover: '#5e4385',
  primaryLight: '#f3f0fa',
};

// Match filter panel / case type chip colors (labelColor where defined)
const LIST_COLOR_PRESETS = APPLICATION_TYPE_ORDER.map(
  (type) => APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color
).filter(Boolean);

const DEFAULT_LIST_COLOR = LIST_COLOR_PRESETS[0] || '#8B5CF6';

function normalizeHexForColorInput(hex) {
  const h = String(hex || DEFAULT_LIST_COLOR).trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(h)) return h;
  if (/^[0-9A-Fa-f]{6}$/.test(h)) return `#${h}`;
  return DEFAULT_LIST_COLOR;
}

function getListColor(list) {
  return list?.color || DEFAULT_LIST_COLOR;
}

function hexToRgba(hex, alpha) {
  const h = String(hex || DEFAULT_LIST_COLOR).replace('#', '');
  if (h.length !== 6) return `rgba(139, 92, 246, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function involvedPartiesMatchQuery(involvedParties, q) {
  if (!involvedParties || typeof involvedParties !== 'object') return false;
  if (involvedParties.project_name && String(involvedParties.project_name).toLowerCase().includes(q)) {
    return true;
  }
  for (const party of Object.values(involvedParties)) {
    if (!party || typeof party !== 'object') continue;
    const human = party.human_name && String(party.human_name).toLowerCase();
    const firm = party.firm_name && String(party.firm_name).toLowerCase();
    if ((human && human.includes(q)) || (firm && firm.includes(q))) return true;
  }
  return false;
}

function reportMatchesSearchQuery(report, q) {
  return (
    (report.case_number && report.case_number.toLowerCase().includes(q)) ||
    (report.display_location && report.display_location.toLowerCase().includes(q)) ||
    (report.one_sentence_summary && report.one_sentence_summary.toLowerCase().includes(q)) ||
    (report.request_summary && report.request_summary.toLowerCase().includes(q)) ||
    (report.subject_addresses && report.subject_addresses.some((a) => a.toLowerCase().includes(q))) ||
    involvedPartiesMatchQuery(report.involved_parties, q)
  );
}

function ListMembershipPill({ list }) {
  const dotColor = getListColor(list);
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: '12px',
        border: `1px solid ${hexToRgba(dotColor, 0.3)}`,
        bgcolor: hexToRgba(dotColor, 0.08),
        maxWidth: '100%',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500, color: colors.text, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {list.name}
      </Typography>
    </Box>
  );
}

function ListMembershipPills({ lists, sx: sxProp }) {
  if (!lists?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, ...sxProp }}>
      {lists.map((l) => (
        <ListMembershipPill key={l.id} list={l} />
      ))}
    </Box>
  );
}

function getApiReportId(app) {
  return app?.published_report_id || app?.id || null;
}

function getActionForReport(app, actionsByReportId) {
  if (!app) return undefined;
  return actionsByReportId[app.id] ?? actionsByReportId[app.published_report_id];
}

function getTriageSection(app, actionsByReportId) {
  const action = getActionForReport(app, actionsByReportId);
  if (!action) return 'uncategorized';
  if (action.triage_status === 'dismissed') return 'dismissed';
  if (action.triage_status === 'kept' || action.is_following) return 'kept';
  return 'uncategorized';
}

const TRIAGE_SECTION_DEFS = [
  { id: 'uncategorized', label: 'UNTRIAGED' },
  { id: 'kept', label: 'KEPT' },
  { id: 'dismissed', label: 'DISMISSED' },
];

const cardActionBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '6px',
  fontSize: '0.72rem',
  flex: 1,
  minWidth: 0,
  px: 0.75,
  py: 0.35,
  whiteSpace: 'nowrap',
};

/** Readable text on solid hex fill (bright neons → dark text). */
function contrastingTextOnHex(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '#ffffff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 165 ? '#111827' : '#ffffff';
}

function ExpandableSection({ title, children, defaultOpen = true, dense = false, sx: sxProp }) {
  const [open, setOpen] = useState(defaultOpen);
  const sectionMb = dense ? 1 : 2;
  const sectionPb = dense ? 1 : 2;
  const headerMb = open ? (dense ? 1 : 2) : 0;
  return (
    <Box sx={{ mb: sectionMb, borderBottom: `1px solid ${colors.border}`, pb: sectionPb, ...sxProp }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: headerMb }}
        onClick={() => setOpen(!open)}
      >
        <Typography
          sx={{
            fontSize: dense ? '0.8rem' : '0.85rem',
            fontWeight: 800,
            color: colors.text,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {open ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: colors.textSecondary }} /> : <KeyboardArrowDownIcon fontSize="small" sx={{ color: colors.textSecondary }} />}
      </Box>
      <Collapse in={open}>
        {children}
      </Collapse>
    </Box>
  );
}

function DetailRow({ label, value, valueNode, dense = false }) {
  return (
    <Box sx={{ mb: dense ? 0.75 : 1.5 }}>
      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: dense ? 0.15 : 0.25, fontWeight: 600 }}>
        {label}
      </Typography>
      {valueNode ? valueNode : (
        <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
          {value || '—'}
        </Typography>
      )}
    </Box>
  );
}

/** v3 may expose text[] or a single string; split newlines for multiple items. */
function normalizeToTrimmedStringList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => (x != null ? String(x).trim() : '')).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  if (/\r?\n/.test(s)) {
    return s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  }
  return [s];
}

/**
 * Zoning fields may be a legacy string or { code_abbreviated, hover_label, raw }.
 * UI shows code_abbreviated; hover_label is the tooltip; raw is DB-only (ignored here).
 */
function zoningDisplayText(z) {
  if (z == null) return '';
  if (typeof z === 'object' && !Array.isArray(z)) {
    const c = z.code_abbreviated;
    if (c != null && String(c).trim()) return String(c).trim();
    return '';
  }
  return String(z).trim();
}

function zoningHoverLabel(z) {
  if (z == null || typeof z !== 'object' || Array.isArray(z)) return '';
  const h = z.hover_label ?? z.hoverLabel;
  if (h == null) return '';
  return String(h).trim();
}

function ZoningTypography({ zoning, variant, sx }) {
  const text = zoningDisplayText(zoning) || '—';
  const tip = zoningHoverLabel(zoning);
  const inner = (
    <Typography variant={variant} sx={sx}>
      {text}
    </Typography>
  );
  if (!tip) return inner;
  return (
    <Tooltip title={tip} enterDelay={400}>
      <Box component="span" sx={{ display: 'inline-block', cursor: 'help' }}>
        {inner}
      </Box>
    </Tooltip>
  );
}

/** Header / list chip: first_activity_date … last_activity_date (v3 only). */
/** Same visual as Gantt markers (diameter, icon, colors from case status fill). */
function TimelineEventMarker({ event, statusColor, circleRef }) {
  const et = String(event.event_type || '').toLowerCase();
  const diam = eventBubbleDiameter(event);
  const showIcon = ICON_EVENT_TYPES.has(et);
  const isMilestone = MILESTONE_EVENT_TYPES.has(et);
  const fs = markerIconFontSizePx(et);
  let icon = <RadioButtonUncheckedIcon />;
  switch (et) {
    case 'initial_filing':
      icon = <PlayArrowIcon />;
      break;
    case 'recommendation_approval':
      icon = <ThumbUpAltOutlinedIcon />;
      break;
    case 'recommendation_denial':
      icon = <ThumbDownAltOutlinedIcon />;
      break;
    case 'approved':
      icon = <CheckIcon />;
      break;
    case 'denied':
      icon = <CloseIcon />;
      break;
    case 'withdrawn':
      icon = <UndoIcon />;
      break;
    default:
      break;
  }
  return (
    <Box
      sx={{
        width: 36,
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: 0.15,
      }}
    >
      <Box
        ref={circleRef}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: diam,
          height: diam,
          borderRadius: '50%',
          bgcolor: statusColor,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: '1.5px solid rgba(255,255,255,0.92)',
          boxShadow:
            isMilestone
              ? '0 0 0 1px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.25)'
              : '0 0 0 1px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.22)',
        }}
      >
        {showIcon
          ? React.cloneElement(icon, {
              sx: {
                fontSize: `${fs}px`,
                color: 'white',
                '& path': { stroke: 'white', strokeWidth: 2 },
              },
            })
          : null}
      </Box>
    </Box>
  );
}

/** Timeline grid + vertical backbone inset from first/last bubble centers (matches Gantt backbone semantics). */
function CaseTimelineBlock({ timelineEvents, timelineStatusColor }) {
  const spineTrackRef = useRef(null);
  const firstCircleRef = useRef(null);
  const lastCircleRef = useRef(null);
  const [spineInset, setSpineInset] = useState({ top: 0, height: 0 });

  useLayoutEffect(() => {
    const track = spineTrackRef.current;
    const first = firstCircleRef.current;
    const last = lastCircleRef.current;
    if (!track || !first || !last) return;

    const measure = () => {
      const tr = track.getBoundingClientRect();
      const fr = first.getBoundingClientRect();
      const lr = last.getBoundingClientRect();
      const firstCy = fr.top + fr.height / 2;
      const lastCy = lr.top + lr.height / 2;
      // Height from first center to last center (do not use track bottom — avoids grid/subpixel mismatch).
      const top = Math.max(0, firstCy - tr.top);
      const height = Math.max(0, lastCy - firstCy);
      setSpineInset({ top, height });
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(track);
    ro.observe(first);
    ro.observe(last);
    return () => ro.disconnect();
  }, [timelineEvents]);

  const showSpine = timelineEvents.length >= 2;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px minmax(0, 1fr)',
        columnGap: 1.25,
        rowGap: 3,
        pl: 1.5,
        ml: 0.5,
        position: 'relative',
      }}
    >
      {timelineEvents.map((event, index) => {
        const et = String(event.event_type || '').toLowerCase();
        const titleBase = (event.label || et || 'Event').trim();
        const vote = event.vote;

        let dateStr = 'Date unknown';
        if (event.date) {
          const shown = formatLocalDateString(event.date);
          if (shown) dateStr = shown;
        }

        const tagChipSx = (hex) => ({
          height: 20,
          fontSize: '0.6rem',
          fontWeight: 700,
          flexShrink: 0,
          bgcolor: `${hex}22`,
          color: hex,
          '& .MuiChip-label': { px: 0.75 },
        });

        return (
          <Fragment key={index}>
            <Box
              sx={{
                gridColumn: 1,
                gridRow: index + 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <TimelineEventMarker
                event={event}
                statusColor={timelineStatusColor}
                circleRef={(el) => {
                  if (index === 0) firstCircleRef.current = el;
                  if (index === timelineEvents.length - 1) lastCircleRef.current = el;
                }}
              />
            </Box>
            <Box sx={{ gridColumn: 2, gridRow: index + 1, minWidth: 0 }}>
              {et === 'initial_filing' ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                    {titleBase}
                  </Typography>
                  <Chip label="INITIAL FILING" size="small" sx={tagChipSx(CASE_STATUS_COLORS.Submitted)} />
                </Box>
              ) : et === 'recommendation_approval' ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                    {titleBase}
                  </Typography>
                  <Chip label="REC. APPROVAL" size="small" sx={tagChipSx(CASE_STATUS_COLORS['Recommendation Approval'])} />
                  {vote ? (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: colors.textSecondary }}>
                      {vote}
                    </Typography>
                  ) : null}
                </Box>
              ) : et === 'recommendation_denial' ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                    {titleBase}
                  </Typography>
                  <Chip label="REC. DENIAL" size="small" sx={tagChipSx(CASE_STATUS_COLORS['Recommendation Denial'])} />
                  {vote ? (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: colors.textSecondary }}>
                      {vote}
                    </Typography>
                  ) : null}
                </Box>
              ) : et === 'approved' || et === 'denied' ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                    {titleBase}
                  </Typography>
                  <Chip
                    label={et === 'approved' ? 'APPROVED' : 'DENIED'}
                    size="small"
                    sx={tagChipSx(getCaseStatusColor(et === 'approved' ? 'Approved' : 'Denied'))}
                  />
                  {vote ? (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: colors.textSecondary }}>
                      {vote}
                    </Typography>
                  ) : null}
                </Box>
              ) : et === 'withdrawn' ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                    {titleBase}
                  </Typography>
                  <Chip label="WITHDRAWN" size="small" sx={tagChipSx(getCaseStatusColor('Withdrawn'))} />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.text, mb: 0.25 }}>
                  {vote ? `${titleBase} — ${vote}` : titleBase}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 500 }}>{dateStr}</Typography>
              {event.body ? (
                <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5, fontWeight: 500 }}>
                  {event.body}
                </Typography>
              ) : null}
              {event.notes ? (
                <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 0.5, display: 'block', opacity: 0.9, whiteSpace: 'pre-wrap' }}>
                  {String(event.notes).trim()}
                </Typography>
              ) : null}
            </Box>
          </Fragment>
        );
      })}
      <Box
        ref={spineTrackRef}
        sx={{
          gridColumn: 1,
          gridRow: '1 / -1',
          position: 'relative',
          zIndex: 0,
          pointerEvents: 'none',
          width: '100%',
          minWidth: 36,
          justifySelf: 'stretch',
        }}
      >
        {showSpine ? (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              // 2px-wide stroke centered in column (avoid transform + % rounding issues).
              left: 'calc(50% - 1px)',
              top: spineInset.top,
              height: spineInset.height,
              width: 2,
              bgcolor: timelineStatusColor,
              opacity: 0.9,
              borderRadius: 1,
              pointerEvents: 'none',
              boxSizing: 'content-box',
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function FiltersActiveChip({ onResetFilters }) {
  if (!onResetFilters) return null;
  const handleReset = (e) => {
    e?.stopPropagation?.();
    onResetFilters();
  };
  return (
    <Chip
      label="Filters active"
      size="small"
      onClick={handleReset}
      onDelete={handleReset}
      deleteIcon={<CloseIcon sx={{ fontSize: '0.85rem !important' }} />}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 600,
        bgcolor: `${colors.primary}12`,
        color: colors.primary,
        cursor: 'pointer',
        '& .MuiChip-deleteIcon': {
          color: colors.primary,
          fontSize: '0.85rem',
          '&:hover': { color: colors.primaryHover },
        },
        '&:hover': { bgcolor: `${colors.primary}22` },
      }}
    />
  );
}

export default function RightPanel({
  applications = [],
  allApplicationsCount = 0,
  allCasesCount = 0,
  followedCount = 0,
  collectionTotalCount = 0,
  filtersActive = false,
  onResetFilters,
  selectedApplication,
  onSelectApplication,
  onSelectProperty,
  onShowOnMap,
  externalOpen,
  onOpenChange,
  embedded,
  isAdmin = false,
  loading = false,
  municipalityOptionsBySlug = {},
  activeCollection = 'all',
  onCollectionChange,
  userActions = [],
  userLists = [],
  actionsByReportId = {},
  onActionChange,
  onListsChange,
}) {
  const listScrollRef = usePersistedScroll(LIST_SCROLL_KEY, {
    enabled: !selectedApplication,
    skipRestore: true, // Virtuoso boots via initialScrollTop / initialTopMostItemIndex
    restoreDeps: [applications.length],
  });
  const listVirtuosoRef = useRef(null);
  const listBootLockedRef = useRef(false);
  const listBootRef = useRef(null);
  const returnFocusAppIdRef = useRef(null);
  const listSnapshottedRef = useRef(false);
  const [flashAppId, setFlashAppId] = useState(null);
  const detailScrollRef = useRef(null);
  // Always open case detail at the top — restoring per-case scroll depth lands you in
  // unrelated sections when hopping between cases.
  useEffect(() => {
    if (!selectedApplication?.id) return;
    const el = detailScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [selectedApplication?.id]);
  const [sitePlanOpen, setSitePlanOpen] = useState(false);

  /** Short laptop CSS height (e.g. 1080p @ 150%) → denser list + detail chrome. Tall (Omen) stays comfortable. */
  const [denseChrome, setDenseChrome] = useState(
    () => typeof window !== 'undefined' && window.innerHeight < 850
  );
  useEffect(() => {
    const onResize = () => setDenseChrome(window.innerHeight < 850);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [panelRef, panelWidth] = useElementWidth();
  /** Narrow right column → tighter list-card padding / action buttons. */
  const narrowPanel = panelWidth > 0 && panelWidth < RIGHT_PANEL_COMFORT_W;
  const cardPadX = narrowPanel ? 1.25 : 2.5;
  const cardPadY = narrowPanel ? 1.25 : 2;
  const cardActionSx = {
    ...cardActionBtnSx,
    fontSize: narrowPanel ? '0.65rem' : '0.72rem',
    px: narrowPanel ? 0.4 : 0.75,
    py: narrowPanel ? 0.25 : 0.35,
    '& .MuiButton-startIcon': {
      marginRight: narrowPanel ? 0.35 : 0.5,
      '& > *:nth-of-type(1)': { fontSize: narrowPanel ? '0.75rem' : '0.85rem' },
    },
  };

  const muniIdToName = useMemo(() => {
    const map = {};
    for (const munis of Object.values(municipalityOptionsBySlug)) {
      for (const m of munis) {
        map[String(m.id)] = m.name;
      }
    }
    return map;
  }, [municipalityOptionsBySlug]);

  const [addToListAnchor, setAddToListAnchor] = useState(null);
  const [addToListReportId, setAddToListReportId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState('recency'); // 'recency' | 'acres'
  const [sortDir, setSortDir] = useState('desc'); // 'desc' | 'asc'
  const [activeTriageSection, setActiveTriageSection] = useState('uncategorized');
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingListInMenu, setCreatingListInMenu] = useState(false);
  const [menuNewListName, setMenuNewListName] = useState('');
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [colorPickerListId, setColorPickerListId] = useState(null);
  const [customColorInput, setCustomColorInput] = useState('');
  const [reportNotes, setReportNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [noteEditSaving, setNoteEditSaving] = useState(false);

  // Pre-compute report→lists membership so we don't filter per card
  const listsForReport = useMemo(() => {
    const map = {};
    (userLists || []).forEach(list => {
      (list.report_ids || []).forEach(rid => {
        if (!map[rid]) map[rid] = [];
        map[rid].push(list);
      });
    });
    return map;
  }, [userLists]);

  const visibleApplications = useMemo(() => {
    let result = applications;

    // Client-side search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((r) => reportMatchesSearchQuery(r, q));
    }

    // Sort
    if (sortMode === 'acres') {
      result = [...result].sort((a, b) => {
        const diff = (Number(b.acres) || 0) - (Number(a.acres) || 0);
        return sortDir === 'asc' ? -diff : diff;
      });
    } else if (sortMode === 'recency' && sortDir === 'asc') {
      result = [...result].reverse();
    }

    return result;
  }, [applications, searchQuery, sortMode, sortDir]);

  const useTriageSections = activeCollection === 'all';

  const triageGroups = useMemo(() => {
    if (!useTriageSections) return null;
    const groups = { uncategorized: [], kept: [], dismissed: [] };
    visibleApplications.forEach((app) => {
      groups[getTriageSection(app, actionsByReportId)].push(app);
    });
    return groups;
  }, [visibleApplications, useTriageSections, actionsByReportId]);

  useEffect(() => {
    if (!triageGroups) return;
    if (activeTriageSection && triageGroups[activeTriageSection]?.length > 0) return;
    const next = TRIAGE_SECTION_DEFS.find(({ id }) => triageGroups[id].length > 0)?.id ?? null;
    setActiveTriageSection(next);
  }, [triageGroups, activeTriageSection]);

  const snapshotListPosition = (app) => {
    listSnapshottedRef.current = true;
    const el = listScrollRef.current;
    if (el && Number.isFinite(el.scrollTop)) {
      writeStoredScroll(LIST_SCROLL_KEY, el.scrollTop);
    }
    if (app?.id != null) {
      returnFocusAppIdRef.current = app.id;
      writeListFocus({
        appId: app.id,
        sectionId: useTriageSections ? activeTriageSection : null,
      });
    }
  };

  // Keep focus id in sync when selection comes from map / timeline / etc.
  useEffect(() => {
    if (!selectedApplication?.id) return;
    returnFocusAppIdRef.current = selectedApplication.id;
    if (listSnapshottedRef.current) {
      // List card already wrote scroll + focus.
      listSnapshottedRef.current = false;
    } else {
      // External select (map/timeline) — don't reuse a stale list offset.
      try {
        localStorage.removeItem(LIST_SCROLL_KEY);
      } catch {
        /* ignore */
      }
      writeListFocus({
        appId: selectedApplication.id,
        sectionId: useTriageSections ? activeTriageSection : null,
      });
    }
    listBootLockedRef.current = false;
    listBootRef.current = null;
  }, [selectedApplication?.id, useTriageSections, activeTriageSection]);

  // Returning to list: open the triage section that holds the focused case.
  useEffect(() => {
    if (selectedApplication || !useTriageSections || !triageGroups) return;
    const focusId = returnFocusAppIdRef.current ?? readListFocus()?.appId;
    if (focusId == null) return;
    const section = TRIAGE_SECTION_DEFS.find(({ id }) =>
      triageGroups[id]?.some((a) => a.id === focusId || String(a.id) === String(focusId))
    )?.id;
    if (section) setActiveTriageSection(section);
  }, [selectedApplication, useTriageSections, triageGroups]);

  // Brief highlight only when returning from detail (not on every list mount).
  const pendingFlashRef = useRef(false);
  useEffect(() => {
    if (selectedApplication) {
      pendingFlashRef.current = true;
      setFlashAppId(null);
      return;
    }
    if (!pendingFlashRef.current) return;
    pendingFlashRef.current = false;
    const focusId = returnFocusAppIdRef.current ?? readListFocus()?.appId;
    if (focusId == null) return;
    setFlashAppId(focusId);
    const t = window.setTimeout(() => setFlashAppId(null), 1800);
    return () => window.clearTimeout(t);
  }, [selectedApplication]);

  // Boot Virtuoso in place: cached scroll if we have it, else center the focused case.
  if (!selectedApplication && !listBootLockedRef.current && visibleApplications.length > 0) {
    listBootLockedRef.current = true;
    const focusMeta = readListFocus();
    const focusId = returnFocusAppIdRef.current ?? focusMeta?.appId ?? null;
    const top = readStoredScroll(LIST_SCROLL_KEY);
    const sectionId = useTriageSections
      ? (focusMeta?.sectionId
        || TRIAGE_SECTION_DEFS.find(({ id }) =>
          triageGroups?.[id]?.some((a) => a.id === focusId || String(a.id) === String(focusId))
        )?.id
        || activeTriageSection)
      : null;

    const listForIndex = useTriageSections && sectionId && triageGroups
      ? triageGroups[sectionId]
      : visibleApplications;
    const focusIndex = focusId != null
      ? listForIndex.findIndex((a) => a.id === focusId || String(a.id) === String(focusId))
      : -1;

    if (top != null) {
      listBootRef.current = { initialScrollTop: top, focusId, sectionId };
    } else if (focusIndex >= 0) {
      listBootRef.current = {
        initialTopMostItemIndex: { index: focusIndex, align: 'center' },
        focusId,
        sectionId,
      };
    } else {
      listBootRef.current = { focusId, sectionId };
    }
  }

  const toggleTriageSection = (sectionId) => {
    setActiveTriageSection((prev) => (prev === sectionId ? null : sectionId));
  };


  const handleAction = async (reportId, { triage_status, is_following }) => {
    // Optimistic update
    const prev = [...(userActions || [])];
    const existing = prev.find(a => a.report_id === reportId);
    let finalTriage = triage_status;
    let finalFollowing = is_following;

    if (finalFollowing === true) finalTriage = 'kept';
    if (finalTriage === 'dismissed') finalFollowing = false;

    if (existing) {
      if (finalTriage === undefined) finalTriage = existing.triage_status;
      if (finalFollowing === undefined) finalFollowing = existing.is_following;
      if (finalFollowing === true) finalTriage = 'kept';
      if (finalTriage === 'dismissed') finalFollowing = false;
    }

    const newAction = {
      report_id: reportId,
      triage_status: finalTriage ?? null,
      is_following: finalFollowing ?? false,
      updated_at: new Date().toISOString(),
    };

    const updated = existing
      ? prev.map(a => a.report_id === reportId ? newAction : a)
      : [...prev, newAction];
    onActionChange(updated);

    if (isUserIdentified()) {
      let action = 'updated';
      if (finalTriage === 'kept') action = 'kept';
      else if (finalTriage === 'dismissed') action = 'dismissed';
      if (finalFollowing === true) action = 'followed';
      else if (is_following === false && existing?.is_following) action = 'unfollowed';
      posthog.capture('case_triaged', { report_id: reportId, action });
    }

    try {
      await apiService.putReportAction(reportId, {
        triage_status: finalTriage ?? null,
        is_following: finalFollowing ?? false,
      });
    } catch (e) {
      onActionChange(prev);
    }
  };

  const handleResetAction = async (reportId) => {
    const prev = [...(userActions || [])];
    onActionChange(prev.filter(a => a.report_id !== reportId));
    try {
      await apiService.deleteReportAction(reportId);
    } catch (e) {
      onActionChange(prev);
    }
  };

  const handleAddToList = async (listId, reportId) => {
    const prevLists = [...userLists];
    const updatedLists = userLists.map(l =>
      l.id === listId ? { ...l, report_ids: [...(l.report_ids || []), reportId] } : l
    );
    onListsChange(updatedLists);
    if (isUserIdentified()) {
      posthog.capture('report_added_to_list', { report_id: reportId, list_id: listId });
    }
    try {
      await apiService.addListItem(listId, reportId);
    } catch (e) {
      onListsChange(prevLists);
    }
  };

  const handleRemoveFromList = async (listId, reportId) => {
    const prevLists = [...userLists];
    const updatedLists = userLists.map(l =>
      l.id === listId ? { ...l, report_ids: (l.report_ids || []).filter(id => id !== reportId) } : l
    );
    onListsChange(updatedLists);
    try {
      await apiService.removeListItem(listId, reportId);
    } catch (e) {
      onListsChange(prevLists);
    }
  };

  const handleDeleteList = async (listId) => {
    const prevLists = [...userLists];
    onListsChange(userLists.filter(l => l.id !== listId));
    if (activeCollection === `list:${listId}`) onCollectionChange('all');
    try {
      await apiService.deleteList(listId);
    } catch (e) {
      onListsChange(prevLists);
    }
  };

  const handleCreateList = async (name, reportIdToAdd = null) => {
    const trimmed = name?.trim();
    if (!trimmed) return null;
    try {
      const created = await apiService.createList(trimmed);
      const newList = { ...created, color: created?.color || DEFAULT_LIST_COLOR, report_ids: reportIdToAdd ? [reportIdToAdd] : [] };
      onListsChange([...userLists, newList]);
      if (reportIdToAdd) {
        apiService.addListItem(created.id, reportIdToAdd).catch(() => {});
      }
      return created;
    } catch (e) {
      return null;
    }
  };

  const handleUpdateList = async (listId, updates) => {
    const prevLists = [...userLists];
    onListsChange(userLists.map((l) => (l.id === listId ? { ...l, ...updates } : l)));
    try {
      const updated = await apiService.updateList(listId, updates);
      if (updated) {
        onListsChange(prevLists.map((l) => (l.id === listId ? { ...l, ...updated, report_ids: l.report_ids } : l)));
      }
    } catch (e) {
      onListsChange(prevLists);
    }
  };

  const startEditListName = (list) => {
    setEditingListId(list.id);
    setEditingListName(list.name || '');
  };

  const saveEditListName = async () => {
    const trimmed = editingListName.trim();
    if (!editingListId || !trimmed) {
      setEditingListId(null);
      setEditingListName('');
      return;
    }
    const list = userLists.find((l) => l.id === editingListId);
    if (list && trimmed !== list.name) {
      await handleUpdateList(editingListId, { name: trimmed });
    }
    setEditingListId(null);
    setEditingListName('');
  };

  const openColorPicker = (event, list) => {
    event.stopPropagation();
    setColorPickerAnchor(event.currentTarget);
    setColorPickerListId(list.id);
    setCustomColorInput(getListColor(list));
  };

  const closeColorPicker = () => {
    setColorPickerAnchor(null);
    setColorPickerListId(null);
    setCustomColorInput('');
  };

  const handleSelectListColor = async (color) => {
    if (!colorPickerListId) return;
    const normalized = color.startsWith('#') ? color.toUpperCase() : `#${color}`.toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(normalized)) return;
    await handleUpdateList(colorPickerListId, { color: normalized });
    closeColorPicker();
  };

  const getNotesReportId = (app) => getApiReportId(app);

  const loadReportNotes = async (reportId) => {
    if (!reportId) {
      setReportNotes([]);
      return;
    }
    setNotesLoading(true);
    try {
      const res = await apiService.getReportNotes(reportId);
      setReportNotes(res?.notes || []);
    } catch (e) {
      setReportNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleSaveNote = async () => {
    const content = noteInput.trim();
    const reportId = getNotesReportId(selectedApplication);
    if (!content || !reportId || noteSaving) return;
    setNoteSaving(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      report_id: reportId,
      content,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setReportNotes((prev) => [optimistic, ...prev]);
    setNoteInput('');
    try {
      const created = await apiService.createReportNote(reportId, content);
      setReportNotes((prev) => [created, ...prev.filter((n) => n.id !== optimistic.id)]);
    } catch (e) {
      setReportNotes((prev) => prev.filter((n) => n.id !== optimistic.id));
      setNoteInput(content);
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    const reportId = getNotesReportId(selectedApplication);
    if (!reportId) return;
    const prev = [...reportNotes];
    setReportNotes((n) => n.filter((note) => note.id !== noteId));
    if (editingNoteId === noteId) {
      setEditingNoteId(null);
      setEditingNoteContent('');
    }
    try {
      await apiService.deleteReportNote(reportId, noteId);
    } catch (e) {
      setReportNotes(prev);
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content || '');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  const handleSaveEditNote = async () => {
    const content = editingNoteContent.trim();
    const reportId = getNotesReportId(selectedApplication);
    if (!content || !reportId || !editingNoteId || noteEditSaving) return;
    const noteId = editingNoteId;
    const prev = [...reportNotes];
    setNoteEditSaving(true);
    setReportNotes((notes) =>
      notes.map((n) => (n.id === noteId ? { ...n, content, updated_at: new Date().toISOString() } : n))
    );
    try {
      const updated = await apiService.updateReportNote(reportId, noteId, content);
      setReportNotes((notes) => notes.map((n) => (n.id === noteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteContent('');
    } catch (e) {
      setReportNotes(prev);
    } finally {
      setNoteEditSaving(false);
    }
  };

  const openAddToListMenu = (event, reportId) => {
    setCreatingListInMenu(false);
    setMenuNewListName('');
    setAddToListAnchor(event.currentTarget);
    setAddToListReportId(reportId);
  };

  const closeAddToListMenu = () => {
    setAddToListAnchor(null);
    setAddToListReportId(null);
    setCreatingListInMenu(false);
    setMenuNewListName('');
  };

  const renderAddToListMenu = () => (
    <Menu
      anchorEl={addToListAnchor}
      open={Boolean(addToListAnchor)}
      onClose={closeAddToListMenu}
      PaperProps={{ sx: { minWidth: 220, maxHeight: 360 } }}
      MenuListProps={{ sx: { py: 0.5 } }}
    >
      {userLists.map((list) => {
        const isInList = (list.report_ids || []).includes(addToListReportId);
        const dotColor = getListColor(list);
        return (
          <MenuItem
            key={list.id}
            dense
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (isInList) handleRemoveFromList(list.id, addToListReportId);
              else handleAddToList(list.id, addToListReportId);
            }}
            sx={{ fontSize: '0.82rem', py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox size="small" checked={isInList} sx={{ p: 0 }} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0, mr: 1 }} />
            <ListItemText primary={list.name} />
          </MenuItem>
        );
      })}
      {userLists.length === 0 && !creatingListInMenu && (
        <MenuItem disabled sx={{ fontSize: '0.82rem' }}>
          <ListItemText>No lists yet</ListItemText>
        </MenuItem>
      )}
      <Divider sx={{ my: 0.5 }} />
      {creatingListInMenu ? (
        <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 0.5, alignItems: 'center' }} onMouseDown={(e) => e.preventDefault()}>
          <TextField
            autoFocus
            size="small"
            placeholder="List name"
            value={menuNewListName}
            onChange={(e) => setMenuNewListName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && menuNewListName.trim()) {
                await handleCreateList(menuNewListName, addToListReportId);
                closeAddToListMenu();
              }
              if (e.key === 'Escape') {
                setCreatingListInMenu(false);
                setMenuNewListName('');
              }
            }}
            InputProps={{ sx: { fontSize: '0.8rem', height: 32 } }}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            disabled={!menuNewListName.trim()}
            onClick={async () => {
              if (menuNewListName.trim()) {
                await handleCreateList(menuNewListName, addToListReportId);
                closeAddToListMenu();
              }
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <MenuItem
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setCreatingListInMenu(true); setMenuNewListName(''); }}
          sx={{ fontSize: '0.82rem' }}
        >
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create new list</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  const renderColorPickerPopover = () => {
    const activeColor = normalizeHexForColorInput(customColorInput).toUpperCase();
    const colorPickerList = userLists.find((l) => l.id === colorPickerListId);
    const currentColor = colorPickerList ? getListColor(colorPickerList).toUpperCase() : activeColor;

    return (
    <Popover
      open={Boolean(colorPickerAnchor)}
      anchorEl={colorPickerAnchor}
      onClose={closeColorPicker}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 1.5, minWidth: 220 } }}
    >
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
        List Color
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary, mb: 0.75 }}>
        Case type colors
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75, mb: 1.5 }}>
        {LIST_COLOR_PRESETS.map((preset, idx) => {
          const presetUpper = preset.toUpperCase();
          const isSelected = currentColor === presetUpper;
          const typeName = APPLICATION_TYPE_ORDER[idx];
          return (
            <Tooltip key={preset} title={typeName || preset} placement="top">
              <Box
                onClick={() => handleSelectListColor(preset)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: preset,
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${colors.text}` : `2px solid ${hexToRgba(preset, 0.25)}`,
                  boxShadow: isSelected ? `0 0 0 2px ${colors.bg}` : 'none',
                  '&:hover': { transform: 'scale(1.08)' },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ pt: 1.25, borderTop: `1px solid ${colors.border}` }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
          Custom color
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box
            component="label"
            sx={{
              position: 'relative',
              width: 44,
              height: 36,
              borderRadius: 1,
              border: `1px solid ${colors.border}`,
              overflow: 'hidden',
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: colors.surface,
              '&:hover': { borderColor: colors.primary },
            }}
          >
            <Box
              component="input"
              type="color"
              value={normalizeHexForColorInput(customColorInput)}
              onChange={(e) => {
                const next = e.target.value.toUpperCase();
                setCustomColorInput(next);
                handleSelectListColor(next);
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                p: 0,
                m: 0,
                border: 'none',
                cursor: 'pointer',
                opacity: 0,
              }}
            />
            <Box sx={{ width: 28, height: 20, borderRadius: 0.5, bgcolor: activeColor, pointerEvents: 'none' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: colors.text }}>
              Pick a color
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: colors.textSecondary, fontFamily: 'monospace' }}>
              {activeColor}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Popover>
    );
  };

  // Sync open state
  useEffect(() => {
    if (typeof externalOpen === 'boolean' && externalOpen === false) {
      // maybe clear selection if closed? Let's leave it as is.
    }
  }, [externalOpen]);

  useEffect(() => {
    setSitePlanOpen(false);
  }, [selectedApplication]);

  useEffect(() => {
    setNoteInput('');
    setEditingNoteId(null);
    setEditingNoteContent('');
    const reportId = getNotesReportId(selectedApplication);
    if (reportId) {
      loadReportNotes(reportId);
    } else {
      setReportNotes([]);
    }
  }, [selectedApplication?.id, selectedApplication?.published_report_id]);

  const formatDate = (s) => {
    if (!s) return '';
    const out = formatLocalDateString(s);
    return out || String(s);
  };

  const renderListCard = (app, index = 0) => {
    const appTypes = app.category_names?.length > 0 ? categoryTagsForDisplay(app.category_names) : ['Case'];
    const statusLabel = app.current_status;
    const statusColor = getCaseStatusColor(statusLabel);
    const muniNames = (app.municipality_ids || [])
      .map((id) => muniIdToName[String(id)])
      .filter(Boolean);
    const zoningFrom = typeof app.current_zoning === 'object' ? app.current_zoning?.code_abbreviated || app.current_zoning?.raw : app.current_zoning;
    const zoningTo = typeof app.proposed_zoning === 'object' ? app.proposed_zoning?.code_abbreviated || app.proposed_zoning?.raw : app.proposed_zoning;
    const zoningLabel = (zoningFrom || zoningTo)
      ? (zoningFrom && zoningTo ? `${zoningFrom} → ${zoningTo}` : zoningFrom || zoningTo)
      : null;
    const lastDate = app.last_activity_date || app.first_activity_date;
    const shortDate = lastDate
      ? new Date(lastDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
      : null;
    const action = getActionForReport(app, actionsByReportId);
    const isKept = action?.triage_status === 'kept';
    const isDismissed = action?.triage_status === 'dismissed';
    const isFollowing = action?.is_following === true;
    const apiReportId = getApiReportId(app);
    const isFlash = flashAppId != null && (app.id === flashAppId || String(app.id) === String(flashAppId));

    return (
      <Box sx={{ px: narrowPanel ? 1.25 : 2, pt: index === 0 ? 2 : 0, pb: 2 }}>
        <Box
          sx={{
            bgcolor: isFlash ? 'rgba(116,84,165,0.14)' : (isDismissed ? '#f9fafb' : colors.bg),
            px: cardPadX,
            py: cardPadY,
            borderRadius: 2,
            border: `1px solid ${isFlash ? '#7454A5' : (isDismissed ? '#e5e7eb' : colors.border)}`,
            opacity: isDismissed ? 0.6 : 1,
            cursor: 'pointer',
            transition: 'background-color 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease',
            minWidth: 0,
            boxShadow: isFlash ? '0 0 0 2px rgba(116,84,165,0.28)' : 'none',
            animation: isFlash ? 'lsCaseFlash 1.6s ease-out' : 'none',
            '@keyframes lsCaseFlash': {
              '0%': {
                backgroundColor: 'rgba(116,84,165,0.28)',
                boxShadow: '0 0 0 3px rgba(116,84,165,0.4)',
              },
              '100%': {
                backgroundColor: 'rgba(116,84,165,0.14)',
                boxShadow: '0 0 0 2px rgba(116,84,165,0.28)',
              },
            },
            '&:hover': {
              borderColor: colors.primary,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            },
          }}
        >
          <Box onClick={() => {
            addUserActionBreadcrumb(`Viewed report detail: ${app.case_number || app.id}`, {
              case_number: app.case_number,
              report_id: app.id,
            });
            if (isUserIdentified()) {
              posthog.capture('report_viewed', {
                report_id: app.id,
                report_type: app.application_type,
                county: app.county_slug,
                source: 'list_card',
              });
            }
            snapshotListPosition(app);
            onSelectApplication(app);
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 600, color: colors.textSecondary, fontSize: '0.8rem', letterSpacing: '0.3px' }}>
                {app.case_number ? `Case ${app.case_number}` : app.project_name || 'Unnamed Case'}
              </Typography>
              {isAdmin && app.is_published !== undefined && (
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: app.is_published ? '#22c55e' : '#f59e0b', flexShrink: 0 }} title={app.is_published ? 'Published' : 'Draft'} />
              )}
              {isFollowing && (
                <FavoriteIcon sx={{ fontSize: '0.85rem', color: '#e11d48' }} />
              )}
            </Box>

            <Typography sx={{ fontWeight: 700, color: colors.text, lineHeight: 1.35, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.92rem' }}>
              {app.one_sentence_summary || (app.case_number ? `Case ${app.case_number}` : 'Unnamed Case')}
            </Typography>

            <Typography sx={{ color: colors.textSecondary, fontSize: '0.82rem', mb: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {app.display_location || (app.subject_addresses && app.subject_addresses[0]) || (app.subject_regions && app.subject_regions[0]) || ''}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
              {appTypes.map((type, i) => {
                const typeColor = APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || colors.primary;
                return (
                  <Chip key={`t-${i}`} label={type.toUpperCase()} size="small" sx={{ height: 24, fontSize: '0.7rem', bgcolor: `${typeColor}18`, color: typeColor, fontWeight: 700, borderRadius: '4px' }} />
                );
              })}
              {muniNames.slice(0, 1).map((name, i) => (
                <Chip key={`m-${i}`} label={name} size="small" sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#f3f4f6', color: '#4b5563', fontWeight: 600, borderRadius: '4px' }} />
              ))}
              {zoningLabel && (
                <Chip label={zoningLabel} size="small" sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#f3f4f6', color: '#4b5563', fontWeight: 600, borderRadius: '4px' }} />
              )}
              {statusLabel && (
                <Chip
                  label={STATUS_BADGE_LABELS[statusLabel] || String(statusLabel).toUpperCase()}
                  size="small"
                  sx={{ height: 24, fontSize: '0.7rem', bgcolor: `${statusColor}15`, color: statusColor, fontWeight: 700, borderRadius: '4px' }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
              {app.residential_units && (
                <Chip label={`${app.residential_units} UNITS`} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, borderRadius: '4px', borderColor: colors.border, color: colors.text }} />
              )}
              {app.acres && (
                <Chip label={`${app.acres} AC`} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, borderRadius: '4px', borderColor: colors.border, color: colors.text }} />
              )}
              {shortDate && (
                <Chip label={shortDate} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, borderRadius: '4px', borderColor: colors.border, color: colors.text }} />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: narrowPanel ? 0.35 : 0.5, mt: 1.25, pt: 1.25, borderTop: `1px solid ${colors.border}`, alignItems: 'center', minWidth: 0 }}>
            <Button
              size="small"
              variant={isFollowing ? 'contained' : 'outlined'}
              startIcon={isFollowing ? <FavoriteIcon sx={{ fontSize: '0.85rem !important' }} /> : <FavoriteBorderIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={(e) => { e.stopPropagation(); handleAction(apiReportId, { is_following: !isFollowing }); }}
              sx={{
                ...cardActionSx,
                ...(isFollowing
                  ? { bgcolor: '#e11d48', borderColor: '#e11d48', color: '#fff', '&:hover': { bgcolor: '#be123c' } }
                  : { color: colors.text, borderColor: colors.border }),
              }}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
            <Button
              size="small"
              variant={isKept ? 'contained' : 'outlined'}
              startIcon={isKept ? <BookmarkIcon sx={{ fontSize: '0.85rem !important' }} /> : <BookmarkBorderIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={(e) => { e.stopPropagation(); handleAction(apiReportId, { triage_status: isKept ? null : 'kept' }); }}
              sx={{
                ...cardActionSx,
                ...(isKept
                  ? { bgcolor: colors.primary, borderColor: colors.primary, color: '#fff', '&:hover': { bgcolor: colors.primaryHover } }
                  : { color: colors.text, borderColor: colors.border }),
              }}
            >
              {isKept ? 'Kept' : 'Keep'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityOffIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={(e) => { e.stopPropagation(); handleAction(apiReportId, { triage_status: 'dismissed' }); }}
              sx={{
                ...cardActionSx,
                color: isDismissed ? '#dc2626' : colors.textSecondary,
                borderColor: isDismissed ? '#dc2626' : colors.border,
              }}
            >
              Dismiss
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlaylistAddIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={(e) => { e.stopPropagation(); openAddToListMenu(e, apiReportId); }}
              sx={{ ...cardActionSx, flex: '0 0 auto', color: colors.text, borderColor: colors.border, px: narrowPanel ? 0.5 : 1.25 }}
            >
              {narrowPanel ? 'List' : '+ List'}
            </Button>
          </Box>

          <ListMembershipPills lists={listsForReport[app.id] || listsForReport[app.published_report_id]} />
        </Box>
      </Box>
    );
  };

  if (selectedApplication) {
    const app = selectedApplication;
    const appTypes = app.category_names?.length > 0 ? categoryTagsForDisplay(app.category_names) : ['Unknown Type'];
    const primaryType = getPriorityApplicationType(app) || appTypes[0] || 'Unknown';
    const notesReportId = getNotesReportId(app);
    const typeStyle = getApplicationStyle(primaryType);
    const typeColor = typeStyle.labelColor || typeStyle.color;
    const isRezoning = primaryType === 'Rezoning';

    // Amendment-case detection (CPA Cat 6) drives two conditional rendering
    // rules:
    //   - includesAmendment → show AMENDMENT CHANGES section (any case touching
    //     CPA, even in combo with other tags like a Rezoning + CPA Map
    //     Amendment bundle)
    //   - isPureAmendment → hide SITE INFO / APPLICANT INFO / PARCELS sections
    //     (jurisdiction-wide cases with no parcel-level data; per the LandSentry
    //     domain reference docs, CPAs are purely-legislative actions that don't
    //     have a subject property the way rezoning/CUP/variance cases do).
    //     Combined cases like Rezoning + CPA Map Amendment still need
    //     site/applicant/parcel info because of their property-tied component.
    // Matches both the full name ("Comprehensive Plan Amendment") as stored in
    // the DB category_names column, and the short form ("CPA") in case any code
    // path uses it.
    const AMENDMENT_TYPE_NAMES = new Set([
      'Comprehensive Plan Amendment', 'CPA',
    ]);
    const categoryNames = Array.isArray(app.category_names) ? app.category_names : [];
    const includesAmendment = categoryNames.some((n) => AMENDMENT_TYPE_NAMES.has(n));
    const isPureAmendment = categoryNames.length > 0 && categoryNames.every((n) => AMENDMENT_TYPE_NAMES.has(n));
    const oneSentence =
      app.one_sentence_summary != null && String(app.one_sentence_summary).trim()
        ? String(app.one_sentence_summary).trim()
        : '';
    const hasZoningPair = Boolean(zoningDisplayText(app.current_zoning) || zoningDisplayText(app.proposed_zoning));
    const statusLabel = app.current_status;
    const date = formatDate(app.last_activity_date || app.first_activity_date);
    const statusColor = getCaseStatusColor(statusLabel);

    const sitePlanSignedUrl = app?.site_plan?.image?.signed_url || null;
    const sitePlanSource = app?.site_plan?.source || null;

    const heroFg = contrastingTextOnHex(typeColor);
    const heroMuted = heroFg === '#ffffff' ? 'rgba(255,255,255,0.75)' : 'rgba(17,24,39,0.65)';
    // Same height breakpoint as list chrome: compact sticky/sections on short laptops only.
    const dd = denseChrome
      ? {
          navH: PANEL_CHROME_HEADER_H,
          stickyPt: 2,
          stickyPb: 2,
          stickyGap: 1.25,
          titleFs: undefined, // MUI h5
          actionFs: '0.78rem',
          actionPy: 0.35,
          chipH: 24,
          heroPx: 1.5,
          heroPt: 1.25,
          heroPb: 1.5,
          heroMb: 2,
          heroTextFs: '1rem',
          heroTextLh: 1.35,
          scrollPt: 2,
          scrollPb: 3,
          contentGap: 1,
          partyMb: 0.75,
          partyLabelMb: 0.15,
          partyMetaMt: 0.15,
          heroDividerPt: 1,
        }
      : {
          navH: PANEL_CHROME_HEADER_H,
          stickyPt: 2.25,
          stickyPb: 2.25,
          stickyGap: 1.5,
          titleFs: '1.65rem',
          actionFs: '0.85rem',
          actionPy: 0.55,
          chipH: 26,
          heroPx: 2,
          heroPt: 1.75,
          heroPb: 2,
          heroMb: 2.5,
          heroTextFs: '1.05rem',
          heroTextLh: 1.5,
          scrollPt: 2.5,
          scrollPb: 3.5,
          contentGap: 2,
          partyMb: 1.5,
          partyLabelMb: 0.25,
          partyMetaMt: 0.25,
          heroDividerPt: 1.5,
        };
    const heroCardSx = {
      bgcolor: typeColor,
      color: heroFg,
      borderRadius: 2,
      boxSizing: 'border-box',
      // Optical equal inset: body line-height adds air above glyphs, so pt < px
      pl: dd.heroPx,
      pr: dd.heroPx,
      pt: dd.heroPt,
      pb: dd.heroPb,
      mb: dd.heroMb,
      boxShadow: `0 4px 12px ${typeColor}55`,
    };
    const heroTextSx = {
      fontWeight: 600,
      fontSize: dd.heroTextFs,
      lineHeight: dd.heroTextLh,
      m: 0,
      p: 0,
    };

    const detailChipSx = {
      height: dd.chipH,
      fontWeight: 700,
      borderRadius: '4px',
      '& .MuiChip-label': { px: 1 },
    };

    const showHero =
      (isRezoning && hasZoningPair)
      || (!!oneSentence && (!isRezoning || !hasZoningPair));

    const hasMappableParcels = Array.isArray(app.parcel_geometries)
      && app.parcel_geometries.some((p) => p?.geometry);

    return (
      <>
      <Box ref={panelRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, bgcolor: colors.bg }}>
        {/* Header Navigation */}
        <Box sx={{ height: dd.navH, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, bgcolor: colors.bg, boxSizing: 'border-box', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 0 }}>
            {onShowOnMap && hasMappableParcels ? (
              <Tooltip title="Show on map" placement="bottom" enterDelay={400}>
                <IconButton
                  size="small"
                  aria-label="Show on map"
                  onClick={() => onShowOnMap(app)}
                  sx={{
                    color: colors.primary,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    p: 0.6,
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f3f4f6', borderColor: colors.primary },
                  }}
                >
                  <PlaceIcon sx={{ fontSize: '1.35rem' }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, minWidth: 0 }}>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<PlaylistAddIcon sx={{ fontSize: '1rem !important', mr: -0.5 }} />} 
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.2rem !important', ml: -0.5 }} />}
              onClick={(e) => openAddToListMenu(e, getApiReportId(app))}
              sx={{ textTransform: 'none', py: 0.25, px: 1.5, color: colors.text, borderColor: colors.border, fontWeight: 600, borderRadius: '20px', fontSize: denseChrome ? '0.75rem' : '0.8rem', whiteSpace: 'nowrap' }}
            >
              Add to List
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              startIcon={<ArrowBackIcon sx={{ fontSize: '1rem !important', mr: -0.5 }} />}
              onClick={() => onSelectApplication(null)} 
              sx={{ textTransform: 'none', py: 0.25, px: 1.5, bgcolor: colors.text, color: '#fff', fontWeight: 600, borderRadius: '6px', fontSize: denseChrome ? '0.75rem' : '0.8rem', boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#000', boxShadow: 'none' } }}
            >
              Full case list
            </Button>
          </Box>
        </Box>

        {/* Static Header Details — even vertical rhythm between rows */}
        <Box
          sx={{
            px: 2,
            pt: dd.stickyPt,
            pb: dd.stickyPb,
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: dd.stickyGap,
          }}
        >
          {/* Title Area */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.text, lineHeight: 1.2, m: 0, ...(dd.titleFs ? { fontSize: dd.titleFs } : null) }}>
              {app.case_number ? `Case ${app.case_number}` : app.application_name || 'Case Application'}
            </Typography>
            {isAdmin && (
              <Chip
                label={app.is_published ? 'Published' : 'Draft'}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: 20,
                  bgcolor: app.is_published ? '#dcfce7' : '#fef3c7',
                  color: app.is_published ? '#166534' : '#92400e',
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: colors.textSecondary, m: 0, lineHeight: 1.35, fontSize: denseChrome ? undefined : '0.9rem' }}>
            {app.display_location
              || (app.subject_addresses && app.subject_addresses[0])
              || (app.subject_regions && app.subject_regions[0])
              || 'Location not specified'}
          </Typography>

          {app.project_name && typeof app.project_name === 'object' && app.project_name.name && (
            <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic', m: 0, lineHeight: 1.35, fontSize: denseChrome ? undefined : '0.9rem' }}>
              &ldquo;{app.project_name.name}&rdquo;
            </Typography>
          )}

          {/* Pills */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', m: 0 }}>
            {appTypes.map((type, i) => {
              const typeColor = APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || colors.primary;
              return (
                <Chip key={i} label={type.toUpperCase()} size="small" sx={{ ...detailChipSx, bgcolor: `${typeColor}15`, color: typeColor }} />
              );
            })}
            {date && <Chip label={date.toUpperCase()} size="small" sx={{ ...detailChipSx, bgcolor: colors.surface, border: `1px solid ${colors.border}`, color: colors.textSecondary }} />}
            {statusLabel ? (
              <Chip label={STATUS_BADGE_LABELS[statusLabel] || String(statusLabel).toUpperCase()} size="small" sx={{ ...detailChipSx, bgcolor: `${statusColor}15`, color: statusColor }} />
            ) : null}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 0.75, m: 0 }}>
            {(() => {
              const detailAction = getActionForReport(app, actionsByReportId);
              const detailIsKept = detailAction?.triage_status === 'kept';
              const detailIsDismissed = detailAction?.triage_status === 'dismissed';
              const detailIsFollowing = detailAction?.is_following === true;
              const apiReportId = getApiReportId(app);
              return (
                <>
                  <Button
                    size="small"
                    variant={detailIsFollowing ? 'contained' : 'outlined'}
                    startIcon={detailIsFollowing ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    onClick={() => handleAction(apiReportId, { is_following: !detailIsFollowing })}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '6px', fontSize: dd.actionFs, flex: 1, py: dd.actionPy,
                      ...(detailIsFollowing ? { bgcolor: '#e11d48', '&:hover': { bgcolor: '#be123c' } } : { color: colors.text, borderColor: colors.border }),
                    }}
                  >
                    {detailIsFollowing ? 'Following' : 'Follow'}
                  </Button>
                  <Button
                    size="small"
                    variant={detailIsKept ? 'contained' : 'outlined'}
                    startIcon={detailIsKept ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    onClick={() => handleAction(apiReportId, { triage_status: detailIsKept ? null : 'kept' })}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '6px', fontSize: dd.actionFs, flex: 1, py: dd.actionPy,
                      ...(detailIsKept ? { bgcolor: colors.primary, '&:hover': { bgcolor: '#5e4385' } } : { color: colors.text, borderColor: colors.border }),
                    }}
                  >
                    {detailIsKept ? 'Kept' : 'Keep'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityOffIcon />}
                    onClick={() => handleAction(apiReportId, { triage_status: 'dismissed' })}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '6px', fontSize: dd.actionFs, flex: 1, py: dd.actionPy,
                      color: detailIsDismissed ? '#dc2626' : colors.textSecondary, borderColor: detailIsDismissed ? '#dc2626' : colors.border,
                    }}
                  >
                    Dismiss
                  </Button>
                </>
              );
            })()}
          </Box>
          {sitePlanSignedUrl ? (
            <Box sx={{ display: 'flex', m: 0 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<MapIcon />}
                onClick={() => setSitePlanOpen((o) => !o)}
                sx={{ textTransform: 'none', bgcolor: '#f59e0b', color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#d97706' }, py: denseChrome ? undefined : 1 }}
              >
                {sitePlanOpen ? 'Hide site plan' : 'Show site plan'}
              </Button>
            </Box>
          ) : null}
        </Box>

        {/* Scrollable Details Content */}
        <Box ref={detailScrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, pt: dd.scrollPt, pb: dd.scrollPb }}>
          {/* Hero: Rezoning with zoning pair → zoning change only; else → type-colored one_sentence */}
          {isRezoning && hasZoningPair ? (
            <Box sx={heroCardSx}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: heroMuted, letterSpacing: '0.05em' }}>
                ZONING CHANGE
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Box sx={{ bgcolor: heroFg === '#ffffff' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.35)', px: 1.5, py: 0.5, borderRadius: 1, minWidth: 60, textAlign: 'center' }}>
                  <ZoningTypography zoning={app.current_zoning} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} />
                </Box>
                <ArrowBackIcon sx={{ transform: 'rotate(180deg)', color: heroMuted, fontSize: '1.2rem' }} />
                <Box sx={{ bgcolor: heroFg === '#ffffff' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.35)', px: 1.5, py: 0.5, borderRadius: 1, minWidth: 60, textAlign: 'center' }}>
                  <ZoningTypography zoning={app.proposed_zoning} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} />
                </Box>
              </Box>
            </Box>
          ) : !isRezoning && oneSentence ? (
            <Box sx={heroCardSx}>
              <Typography variant="body1" sx={heroTextSx}>
                {oneSentence}
              </Typography>
            </Box>
          ) : isRezoning && !hasZoningPair && oneSentence ? (
            <Box sx={heroCardSx}>
              <Typography variant="body1" sx={heroTextSx}>
                {oneSentence}
              </Typography>
            </Box>
          ) : null}

          {/* Site Info / Applicant Info / Parcels: hidden for pure amendment
              cases (Cat 6 CPA only). These are jurisdiction-wide legislative
              actions with no parcel-level subject. Combined cases like
              Rezoning + CPA Map Amendment still show these sections because of
              the property-tied component. */}
          {!isPureAmendment && (
            <ExpandableSection
              title="SITE INFO"
              dense={denseChrome}
              sx={showHero ? { borderTop: `1px solid ${colors.border}`, pt: dd.heroDividerPt } : undefined}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: dd.contentGap }}>
                <DetailRow dense={denseChrome} label="ACRES" value={app.acres} />
                {!isRezoning ? (
                  <DetailRow
                    dense={denseChrome}
                    label="CURRENT ZONING"
                    valueNode={<ZoningTypography zoning={app.current_zoning} variant="body2" sx={{ color: colors.text, fontWeight: 500 }} />}
                  />
                ) : null}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: dd.contentGap }}>
                  <DetailRow dense={denseChrome} label="EXISTING USE" value={app.existing_use} />
                  <DetailRow dense={denseChrome} label="PROPOSED USE" value={app.proposed_use} />
                </Box>
                <DetailRow dense={denseChrome} label="ADDRESS" value={(app.subject_addresses || [])[0]} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: dd.contentGap }}>
                  <DetailRow dense={denseChrome} label="PROPOSED GROSS DENSITY (DU/AC)" value={app.proposed_density} />
                  <DetailRow dense={denseChrome} label="UNITS" value={app.residential_units} />
                </Box>
              </Box>
            </ExpandableSection>
          )}

          {!isPureAmendment && (
            <ExpandableSection title="INVOLVED PARTIES" dense={denseChrome}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: dd.contentGap }}>
                {(() => {
                  const ip = app.involved_parties;
                  if (!ip) return <DetailRow dense={denseChrome} label="PARTIES" value={null} />;

                  const roleLabels = {
                    owner: 'OWNER',
                    applicant: 'APPLICANT',
                    representative: 'REPRESENTATIVE',
                    engineer: 'ENGINEER',
                    architect: 'ARCHITECT',
                    surveyor: 'SURVEYOR',
                    planner: 'PLANNER',
                  };

                  const renderParty = (roleKey, party) => {
                    if (!party) return null;
                    const label = roleLabels[roleKey] || String(roleKey).replace(/_/g, ' ').toUpperCase();
                    const name = party.human_name && party.firm_name
                      ? `${party.human_name}, ${party.firm_name}`
                      : party.human_name || party.firm_name || null;
                    const contactParts = [
                      party.phone,
                      party.email,
                      party.website_url,
                    ].filter(Boolean);
                    const contact = contactParts.length > 0 ? contactParts.join(' · ') : null;

                    return (
                      <Box key={roleKey} sx={{ mb: dd.partyMb }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: dd.partyLabelMb, fontWeight: 600 }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                          {name || '—'}
                        </Typography>
                        {contact && (
                          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: dd.partyMetaMt }}>
                            {contact}
                          </Typography>
                        )}
                        {party.address && (
                          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: dd.partyMetaMt }}>
                            {party.address}
                          </Typography>
                        )}
                      </Box>
                    );
                  };

                  // Render known roles in priority order, then any extras
                  const priorityOrder = ['applicant', 'owner', 'representative', 'engineer', 'architect', 'surveyor', 'planner'];
                  const knownRoles = priorityOrder.filter(r => ip && ip[r]);
                  const extraRoles = ip ? Object.keys(ip).filter(r => !priorityOrder.includes(r) && r !== 'project_name') : [];
                  const allRoles = [...knownRoles, ...extraRoles];

                  return (
                    <>
                      {allRoles.map(role => renderParty(role, ip[role]))}
                    </>
                  );
                })()}
              </Box>
            </ExpandableSection>
          )}

          {!isPureAmendment && (
            <ExpandableSection title="PARCELS" defaultOpen={false} dense={denseChrome}>
              {app.parcel_geometries && app.parcel_geometries.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: dd.contentGap }}>
                  {app.parcel_geometries.map((p, i) => (
                    <Typography key={i} variant="body2" sx={{ color: colors.text, p: 1, bgcolor: colors.surface, borderRadius: 1 }}>
                      Parcel ID: {p.parcel_id || 'Unknown'}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  {/* User will add content here */}
                </Typography>
              )}
            </ExpandableSection>
          )}

          <ExpandableSection
            title="TIMELINE"
            defaultOpen={true}
            dense={denseChrome}
            sx={showHero && isPureAmendment ? { borderTop: `1px solid ${colors.border}`, pt: dd.heroDividerPt } : undefined}
          >
            {(() => {
              const timelineEvents = sortEventsByDate(getTimelineEventList(app.timeline));
              if (timelineEvents.length === 0) {
                return (
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    Timeline data not available.
                  </Typography>
                );
              }
              const timelineStatusColor = getCaseStatusColor(app.current_status);
              return (
                <CaseTimelineBlock timelineEvents={timelineEvents} timelineStatusColor={timelineStatusColor} />
              );
            })()}
          </ExpandableSection>

          <ExpandableSection title="SOURCES" defaultOpen={true} dense={denseChrome}>
            {(() => {
              const sourceItems = getApplicationSourceItems(app);
              return sourceItems.length > 0 ? (
                <SourceDocumentsList items={sourceItems} variant="panel" colors={colors} />
              ) : (
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  No sources available.
                </Typography>
              );
            })()}
          </ExpandableSection>

          {(() => {
            const recs = normalizeToTrimmedStringList(app.board_recommendations);
            const conds = normalizeToTrimmedStringList(app.conditions_for_approval);
            const amendmentChanges = normalizeToTrimmedStringList(app.amendment_changes);
            const opp =
              app.opposition_received != null && String(app.opposition_received).trim()
                ? String(app.opposition_received).trim()
                : '';
            const sup =
              app.support_received != null && String(app.support_received).trim()
                ? String(app.support_received).trim()
                : '';
            const req =
              app.request_summary != null && String(app.request_summary).trim()
                ? String(app.request_summary).trim()
                : '';
            const hasSummary =
              !!(req || opp || sup || recs.length || conds.length || amendmentChanges.length);
            return (
              <>
                {hasSummary ? (
                  <ExpandableSection title="SUMMARY" defaultOpen dense={denseChrome}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dd.contentGap }}>
                      <DetailRow dense={denseChrome} label="REQUEST SUMMARY" value={req} />
                      {includesAmendment && amendmentChanges.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.textSecondary, display: 'block', mb: 0.5, fontWeight: 600 }}
                          >
                            AMENDMENT CHANGES
                          </Typography>
                          <Box
                            component="ol"
                            sx={{
                              m: 0,
                              pl: 2.75,
                              color: colors.text,
                              fontWeight: 500,
                              listStyleType: 'decimal',
                              listStylePosition: 'outside',
                              '& li': { mb: 0.5, display: 'list-item' },
                            }}
                          >
                            {amendmentChanges.map((c, i) => (
                              <Box key={i} component="li" sx={{ pl: 0.5 }}>
                                <Typography component="span" variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                                  {c}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                      {opp ? <DetailRow dense={denseChrome} label="OPPOSITION RECEIVED" value={opp} /> : null}
                      {sup ? <DetailRow dense={denseChrome} label="SUPPORT RECEIVED" value={sup} /> : null}
                      {recs.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.textSecondary, display: 'block', mb: 0.5, fontWeight: 600 }}
                          >
                            BOARD RECOMMENDATIONS
                          </Typography>
                          <Box
                            component="ol"
                            sx={{
                              m: 0,
                              pl: 2.75,
                              color: colors.text,
                              fontWeight: 500,
                              listStyleType: 'decimal',
                              listStylePosition: 'outside',
                              '& li': { mb: 0.5, display: 'list-item' },
                            }}
                          >
                            {recs.map((r, i) => (
                              <Box key={i} component="li" sx={{ pl: 0.5 }}>
                                <Typography component="span" variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                                  {r}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                      {conds.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.textSecondary, display: 'block', mb: 0.5, fontWeight: 600 }}
                          >
                            CONDITIONS FOR APPROVAL
                          </Typography>
                          <Box
                            component="ol"
                            sx={{
                              m: 0,
                              pl: 2.75,
                              color: colors.text,
                              fontWeight: 500,
                              listStyleType: 'decimal',
                              listStylePosition: 'outside',
                              '& li': { mb: 0.5, display: 'list-item' },
                            }}
                          >
                            {conds.map((c, i) => (
                              <Box key={i} component="li" sx={{ pl: 0.5 }}>
                                <Typography component="span" variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                                  {c}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  </ExpandableSection>
                ) : null}

                <Box sx={{
                  mb: denseChrome ? 2 : 2.5,
                  pt: denseChrome ? 0.5 : 1,
                }}>
                  <Typography
                    sx={{
                      fontSize: denseChrome ? '0.8rem' : '0.85rem',
                      fontWeight: 800,
                      color: colors.text,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                      mb: denseChrome ? 1 : 1.5,
                      display: 'block',
                    }}
                  >
                    NOTES
                  </Typography>

            {!notesReportId ? (
              <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
                Notes are available after this report is published.
              </Typography>
            ) : (
              <>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={8}
              placeholder="Add a note..."
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveNote();
                }
              }}
              disabled={noteSaving}
              variant="outlined"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.surface,
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.textSecondary },
                  '&.Mui-focused fieldset': { borderColor: colors.primary },
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                size="small"
                variant="contained"
                disabled={!noteInput.trim() || noteSaving}
                onClick={handleSaveNote}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: colors.primary,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: colors.primaryHover, boxShadow: 'none' },
                }}
              >
                {noteSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            {notesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} sx={{ color: colors.primary }} />
              </Box>
            ) : reportNotes.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
                No notes yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {reportNotes.map((note) => {
                  const isEditing = editingNoteId === note.id;
                  const wasEdited = note.updated_at && note.created_at
                    && new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000;
                  return (
                  <Box
                    key={note.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${isEditing ? colors.primary : colors.border}`,
                      bgcolor: colors.surface,
                      position: 'relative',
                      '&:hover .note-action-btn': { opacity: 1 },
                    }}
                  >
                    {isEditing ? (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={8}
                          value={editingNoteContent}
                          onChange={(e) => setEditingNoteContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveEditNote();
                            }
                            if (e.key === 'Escape') handleCancelEditNote();
                          }}
                          disabled={noteEditSaving}
                          autoFocus
                          variant="outlined"
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: colors.bg,
                              fontSize: '0.875rem',
                              '& fieldset': { borderColor: colors.border },
                              '&.Mui-focused fieldset': { borderColor: colors.primary },
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelEditNote}
                            disabled={noteEditSaving}
                            sx={{ textTransform: 'none', fontWeight: 600, borderColor: colors.border, color: colors.textSecondary }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveEditNote}
                            disabled={!editingNoteContent.trim() || noteEditSaving}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              bgcolor: colors.primary,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: colors.primaryHover, boxShadow: 'none' },
                            }}
                          >
                            {noteEditSaving ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.5, pr: 6 }}>
                          {note.content}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 0.75, display: 'block' }}>
                          {formatRelativeTime(note.updated_at || note.created_at)}
                          {wasEdited ? ' · edited' : ''}
                        </Typography>
                        <IconButton
                          className="note-action-btn"
                          size="small"
                          onClick={() => handleStartEditNote(note)}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 34,
                            opacity: 0,
                            transition: 'opacity 0.15s',
                            color: colors.textSecondary,
                            '&:hover': { color: colors.primary },
                          }}
                        >
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton
                          className="note-action-btn"
                          size="small"
                          onClick={() => handleDeleteNote(note.id)}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            opacity: 0,
                            transition: 'opacity 0.15s',
                            color: colors.textSecondary,
                            '&:hover': { color: '#dc2626' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                  );
                })}
              </Box>
            )}
              </>
            )}
          </Box>
              </>
            );
          })()}
        </Box>

        {/* Site Plan Popup */}
        <Dialog
          open={sitePlanOpen && Boolean(sitePlanSignedUrl)}
          onClose={() => setSitePlanOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { bgcolor: colors.bg } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 700,
              color: colors.text,
              borderBottom: `1px solid ${colors.border}`,
              py: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.text }}>
                Site Plan
              </Typography>
              {sitePlanSource ? (
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {sitePlanSource.label}
                  {sitePlanSource.page ? ` · p. ${sitePlanSource.page}` : ''}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={() => setSitePlanOpen(false)} size="small" sx={{ color: colors.textSecondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, bgcolor: colors.surface, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {sitePlanSignedUrl ? (
              <Box
                component="img"
                src={sitePlanSignedUrl}
                alt="Site plan"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  boxShadow: `0 2px 8px ${colors.border}`,
                }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
      {renderAddToListMenu()}
      {renderColorPickerPopover()}
      </>
    );
  }

  // Collection label helper
  const collectionLabel = activeCollection === 'all' ? 'All Cases'
    : activeCollection === 'followed' ? 'Followed'
    : userLists.find(l => `list:${l.id}` === activeCollection)?.name || 'List';

  const totalInCollection = collectionTotalCount || allApplicationsCount || applications.length;
  const searchActive = searchQuery.trim().length > 0;
  const isListOrFollowed = activeCollection !== 'all';
  const showingFilteredCount =
    visibleApplications.length < totalInCollection ||
    (searchActive && visibleApplications.length < applications.length);
  const countSuffixParts = [];
  if (searchActive && visibleApplications.length < applications.length) countSuffixParts.push('search');
  if (filtersActive && isListOrFollowed) countSuffixParts.push('filtered');

  const countLabel = showingFilteredCount
    ? `${visibleApplications.length} of ${totalInCollection}${countSuffixParts.length ? ` · ${countSuffixParts.join(' · ')}` : ''}`
    : `${visibleApplications.length}`;

  // Comfortable on Omen-class height; compact only on short CSS viewports (e.g. 1080p @ 150%).
  const lc = denseChrome
    ? {
        sectionPt: 1,
        sectionPb: 0.5,
        headerMb: 0.25,
        headerFs: '0.62rem',
        newFs: '0.68rem',
        pinPy: 0.25,
        pinFs: '0.78rem',
        pinCountFs: '0.68rem',
        pinDot: 7,
        pinIcon: '0.7rem',
        createH: 26,
        createFs: '0.75rem',
        listsMaxH: 84,
        rowPy: 0.2,
        rowFs: '0.75rem',
        rowCountFs: '0.65rem',
        rowIcon: '0.75rem',
        editH: 24,
        sortGap: 0.75,
        sortPt: 0.75,
        sortPb: 1,
        sortFs: '0.65rem',
        sortBtn: 18,
        searchH: 28,
        searchFs: '0.75rem',
        searchIcon: '0.9rem',
        countPy: 0.5,
        countFs: '0.7rem',
      }
    : {
        sectionPt: 1.25,
        sectionPb: 0.75,
        headerMb: 0.5,
        headerFs: '0.7rem',
        newFs: '0.78rem',
        pinPy: 0.45,
        pinFs: '0.88rem',
        pinCountFs: '0.75rem',
        pinDot: 8,
        pinIcon: '0.85rem',
        createH: 30,
        createFs: '0.8rem',
        listsMaxH: 168,
        rowPy: 0.4,
        rowFs: '0.85rem',
        rowCountFs: '0.72rem',
        rowIcon: '0.85rem',
        editH: 28,
        sortGap: 1,
        sortPt: 1,
        sortPb: 1.25,
        sortFs: '0.72rem',
        sortBtn: 22,
        searchH: 34,
        searchFs: '0.82rem',
        searchIcon: '1rem',
        countPy: 0.65,
        countFs: '0.78rem',
      };

  // List View
  return (
    <>
    <Box ref={panelRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, bgcolor: colors.surface }}>
      {/* Collection selector + controls */}
      <Box sx={{ borderBottom: `1px solid ${colors.border}`, bgcolor: colors.bg }}>
        {/* Lists — comfortable on tall viewports; denser + ~3-row scroll on short */}
        <Box sx={{ px: 2, pt: lc.sectionPt, pb: lc.sectionPb }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: lc.headerMb }}>
            <Typography sx={{ fontSize: lc.headerFs, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Lists
            </Typography>
            <Typography
              onClick={() => { setCreatingList(true); setNewListName(''); }}
              sx={{ fontSize: lc.newFs, fontWeight: 600, color: colors.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              + New
            </Typography>
          </Box>
          {/* All Cases — system row uses an icon (like Followed), not a color dot (those are for user lists). */}
          <Box
            onClick={() => onCollectionChange?.('all')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: lc.pinPy, px: 0.5, borderRadius: 1, cursor: 'pointer', bgcolor: activeCollection === 'all' ? `${colors.primary}08` : 'transparent', '&:hover': { bgcolor: '#f9fafb' } }}
          >
            <ListIcon sx={{ fontSize: lc.pinIcon, color: activeCollection === 'all' ? colors.text : '#d1d5db' }} />
            <Typography sx={{ fontSize: lc.pinFs, fontWeight: activeCollection === 'all' ? 700 : 500, color: colors.text, flex: 1, lineHeight: 1.3 }}>All Cases</Typography>
            {allCasesCount > 0 && (
              <Typography sx={{ fontSize: lc.pinCountFs, color: colors.textSecondary }}>({allCasesCount})</Typography>
            )}
          </Box>
          {/* Followed */}
          <Box
            onClick={() => onCollectionChange?.('followed')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: lc.pinPy, px: 0.5, borderRadius: 1, cursor: 'pointer', bgcolor: activeCollection === 'followed' ? '#fdf2f8' : 'transparent', '&:hover': { bgcolor: '#f9fafb' } }}
          >
            <FavoriteIcon sx={{ fontSize: lc.pinIcon, color: activeCollection === 'followed' ? '#e11d48' : '#d1d5db' }} />
            <Typography sx={{ fontSize: lc.pinFs, fontWeight: activeCollection === 'followed' ? 700 : 500, color: colors.text, flex: 1, lineHeight: 1.3 }}>Followed</Typography>
            {followedCount > 0 && (
              <Typography sx={{ fontSize: lc.pinCountFs, color: colors.textSecondary }}>({followedCount})</Typography>
            )}
          </Box>
          {creatingList && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', my: 0.25, pl: 0.5 }}>
                <TextField
                  autoFocus
                  size="small"
                  placeholder="List name"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newListName.trim()) {
                      await handleCreateList(newListName);
                      setCreatingList(false);
                      setNewListName('');
                    }
                    if (e.key === 'Escape') {
                      setCreatingList(false);
                      setNewListName('');
                    }
                  }}
                  InputProps={{ sx: { fontSize: lc.createFs, height: lc.createH, borderRadius: '6px', bgcolor: '#f9fafb' } }}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  disabled={!newListName.trim()}
                  onClick={async () => {
                    if (newListName.trim()) {
                      await handleCreateList(newListName);
                      setCreatingList(false);
                      setNewListName('');
                    }
                  }}
                  sx={{ color: colors.primary, p: 0.35 }}
                >
                  <CheckIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <IconButton size="small" onClick={() => { setCreatingList(false); setNewListName(''); }} sx={{ p: 0.35 }}>
                  <CloseIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            )}
            {/* Custom lists: ~3 rows (short) / ~6 rows (tall) then scroll */}
            <Box
              sx={{
                maxHeight: lc.listsMaxH,
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
            {userLists.map(list => {
              const itemCount = (list.report_ids || []).length;
              const isActive = activeCollection === `list:${list.id}`;
              const dotColor = getListColor(list);
              const isEditing = editingListId === list.id;
              return (
                <Box
                  key={list.id}
                  onClick={() => { if (!isEditing) onCollectionChange?.(`list:${list.id}`); }}
                  onDoubleClick={(e) => { e.stopPropagation(); startEditListName(list); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: lc.rowPy,
                    px: 0.5,
                    borderRadius: 1,
                    cursor: isEditing ? 'default' : 'pointer',
                    bgcolor: isActive ? `${colors.primary}08` : 'transparent',
                    '&:hover': { bgcolor: '#f9fafb' },
                    '&:hover .list-edit-actions': { opacity: 1 },
                  }}
                >
                  <Box
                    onClick={(e) => openColorPicker(e, list)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: dotColor,
                      flexShrink: 0,
                      cursor: 'pointer',
                      border: `1px solid ${hexToRgba(dotColor, 0.4)}`,
                      '&:hover': { transform: 'scale(1.15)' },
                    }}
                  />
                  <ListIcon sx={{ fontSize: lc.rowIcon, color: isActive ? colors.primary : colors.textSecondary }} />
                  {isEditing ? (
                    <TextField
                      autoFocus
                      size="small"
                      value={editingListName}
                      onChange={(e) => setEditingListName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditListName();
                        if (e.key === 'Escape') { setEditingListId(null); setEditingListName(''); }
                      }}
                      onBlur={saveEditListName}
                      onClick={(e) => e.stopPropagation()}
                      InputProps={{ sx: { fontSize: lc.rowFs, height: lc.editH, py: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: lc.rowFs, fontWeight: isActive ? 700 : 500, color: colors.text, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                      {list.name}
                    </Typography>
                  )}
                  {!isEditing && (
                    <Box className="list-edit-actions" sx={{ display: 'flex', gap: 0, opacity: 0, transition: 'opacity 0.15s' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); startEditListName(list); }}
                        sx={{ p: 0.15, color: colors.textSecondary }}
                      >
                        <EditIcon sx={{ fontSize: lc.rowIcon }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => openColorPicker(e, list)}
                        sx={{ p: 0.15, color: colors.textSecondary }}
                      >
                        <PaletteIcon sx={{ fontSize: lc.rowIcon }} />
                      </IconButton>
                    </Box>
                  )}
                  {itemCount > 0 && !isEditing && (
                    <Typography sx={{ fontSize: lc.rowCountFs, color: colors.textSecondary }}>({itemCount})</Typography>
                  )}
                </Box>
              );
            })}
            {userLists.length === 0 && (
              <Typography sx={{ fontSize: lc.rowFs, color: colors.textSecondary, pl: 0.5, fontStyle: 'italic' }}>No lists yet</Typography>
            )}
            </Box>
        </Box>

        {/* Sort + Search */}
        <Box sx={{ px: 2, pb: lc.sortPb, display: 'flex', flexDirection: 'column', gap: lc.sortGap, borderTop: `1px solid ${colors.border}`, pt: lc.sortPt }}>
          {/* Sort row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: lc.sortFs, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase' }}>Sort:</Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, border: `1.5px solid ${sortMode === 'acres' ? colors.text : colors.border}`, borderRadius: '4px', px: 0.5, py: 0.15 }}>
              <Typography sx={{ fontSize: lc.sortFs, fontWeight: 700, color: colors.text, textTransform: 'uppercase' }}>Acres</Typography>
              <Box onClick={() => { setSortMode('acres'); setSortDir('desc'); }} sx={{ width: lc.sortBtn, height: lc.sortBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, borderRadius: '3px', cursor: 'pointer', bgcolor: sortMode === 'acres' && sortDir === 'desc' ? '#e5e7eb' : 'transparent' }}>
                <Typography sx={{ fontSize: lc.sortFs, lineHeight: 1 }}>↓</Typography>
              </Box>
              <Box onClick={() => { setSortMode('acres'); setSortDir('asc'); }} sx={{ width: lc.sortBtn, height: lc.sortBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, borderRadius: '3px', cursor: 'pointer', bgcolor: sortMode === 'acres' && sortDir === 'asc' ? '#e5e7eb' : 'transparent' }}>
                <Typography sx={{ fontSize: lc.sortFs, lineHeight: 1 }}>↑</Typography>
              </Box>
            </Box>
            <Box sx={{ width: 4 }} />
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, border: `1.5px solid ${sortMode === 'recency' ? colors.text : colors.border}`, borderRadius: '4px', px: 0.5, py: 0.15 }}>
              <Typography sx={{ fontSize: lc.sortFs, fontWeight: 700, color: colors.text, textTransform: 'uppercase' }}>Recency</Typography>
              <Box onClick={() => { setSortMode('recency'); setSortDir('desc'); }} sx={{ width: lc.sortBtn, height: lc.sortBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, borderRadius: '3px', cursor: 'pointer', bgcolor: sortMode === 'recency' && sortDir === 'desc' ? '#e5e7eb' : 'transparent' }}>
                <Typography sx={{ fontSize: lc.sortFs, lineHeight: 1 }}>↓</Typography>
              </Box>
              <Box onClick={() => { setSortMode('recency'); setSortDir('asc'); }} sx={{ width: lc.sortBtn, height: lc.sortBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, borderRadius: '3px', cursor: 'pointer', bgcolor: sortMode === 'recency' && sortDir === 'asc' ? '#e5e7eb' : 'transparent' }}>
                <Typography sx={{ fontSize: lc.sortFs, lineHeight: 1 }}>↑</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            {searchActive && visibleApplications.length !== applications.length && (
              <Typography sx={{ fontSize: lc.sortFs, color: colors.textSecondary }}>
                {visibleApplications.length} match
              </Typography>
            )}
          </Box>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search cases, addresses, applicants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: lc.searchIcon, color: colors.textSecondary }} />
                </InputAdornment>
              ),
              sx: { fontSize: lc.searchFs, height: lc.searchH, borderRadius: '6px' },
            }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f9fafb' } }}
          />
        </Box>

        {/* Results count bar */}
        <Box sx={{ px: 2, py: lc.countPy, bgcolor: '#f9fafb', borderTop: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: colors.text, fontSize: lc.countFs, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {collectionLabel} ({countLabel})
          </Typography>
          {filtersActive && <FiltersActiveChip onResetFilters={onResetFilters} />}
        </Box>
      </Box>

      {/* Card list */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
            <CircularProgress size={28} sx={{ color: colors.primary }} />
          </Box>
        )}
        {!loading && visibleApplications.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {activeCollection === 'followed' ? 'No followed cases yet.' : 'No cases match the selected filters.'}
            </Typography>
          </Box>
        )}
        {!loading && useTriageSections && triageGroups && visibleApplications.length > 0 && (
          TRIAGE_SECTION_DEFS.map(({ id, label }) => {
            const count = triageGroups[id].length;
            if (count === 0) return null;
            const isOpen = activeTriageSection === id;
            const boot = listBootRef.current;
            const sectionBootScroll =
              isOpen && boot?.sectionId === id && boot.initialScrollTop != null
                ? boot.initialScrollTop
                : undefined;
            const sectionBootIndex =
              isOpen && boot?.initialTopMostItemIndex && (boot.sectionId == null || boot.sectionId === id)
                ? boot.initialTopMostItemIndex
                : (isOpen && boot?.focusId
                  ? (() => {
                      const idx = triageGroups[id].findIndex(
                        (a) => a.id === boot.focusId || String(a.id) === String(boot.focusId)
                      );
                      return idx >= 0 ? { index: idx, align: 'center' } : undefined;
                    })()
                  : undefined);
            return (
              <Fragment key={id}>
                <Box
                  onClick={() => toggleTriageSection(id)}
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    bgcolor: isOpen ? '#f3f4f6' : colors.bg,
                    borderBottom: `1px solid ${colors.border}`,
                    '&:hover': { bgcolor: '#f3f4f6' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isOpen ? colors.text : colors.textSecondary, letterSpacing: '0.06em' }}>
                    {label} ({count})
                  </Typography>
                  {isOpen
                    ? <KeyboardArrowUpIcon sx={{ fontSize: '1rem', color: colors.textSecondary }} />
                    : <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: colors.textSecondary }} />}
                </Box>
                {isOpen && (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <Virtuoso
                      key={id}
                      ref={listVirtuosoRef}
                      data={triageGroups[id]}
                      overscan={200}
                      style={{ height: '100%' }}
                      computeItemKey={(index, app) => `card-${app.id}`}
                      itemContent={(index, app) => renderListCard(app, index)}
                      initialScrollTop={sectionBootScroll}
                      initialTopMostItemIndex={sectionBootScroll != null ? undefined : sectionBootIndex}
                      scrollerRef={(el) => {
                        listScrollRef.current = el;
                      }}
                    />
                  </Box>
                )}
              </Fragment>
            );
          })
        )}
        {!loading && !useTriageSections && visibleApplications.length > 0 && (
          <Virtuoso
            ref={listVirtuosoRef}
            data={visibleApplications}
            overscan={200}
            style={{ height: '100%' }}
            computeItemKey={(index, app) => `card-${app.id}`}
            itemContent={(index, app) => renderListCard(app, index)}
            initialScrollTop={listBootRef.current?.initialScrollTop}
            initialTopMostItemIndex={
              listBootRef.current?.initialScrollTop != null
                ? undefined
                : listBootRef.current?.initialTopMostItemIndex
            }
            scrollerRef={(el) => {
              listScrollRef.current = el;
            }}
          />
        )}
      </Box>
    </Box>
    {renderAddToListMenu()}
    {renderColorPickerPopover()}
    </>
  );
}